import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserLocation } from '../context/LocationContext';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Play, Heart, Share2, ShoppingBag, TrendingUp, Zap, Sparkles, MapPin,
  Music, Video, Palette, Camera, Mic2, Shirt, Radio, Wrench, Star,
  Loader2, ChevronRight, Flame, Music2
} from 'lucide-react';

const SERVICE_ICONS = {
  beats: Music2, studio: Mic2, mixing: Wrench, video: Video, design: Palette,
  photo: Camera, clothing: Shirt, flyer: Star, podcast: Radio, other: Music,
};

const SORTS = [
  { key: 'newest', label: 'Newest', icon: Sparkles },
  { key: 'trending', label: 'Trending', icon: Flame },
  { key: 'most_streamed', label: 'Most Streamed', icon: TrendingUp },
  { key: 'local', label: 'Local', icon: MapPin },
  { key: 'promoted', label: 'Promoted', icon: Zap },
];

function coverOf(t) {
  return t.cover_art_url || t.cover_url || t.thumbnail_url || t.poster_url || t.thumbnail;
}

function ReleaseCard({ item, kind, onPlay, onSave, onShare, onBuy }) {
  const cover = coverOf(item);
  const isVideo = kind === 'video';
  return (
    <Card className="group relative overflow-hidden rounded-2xl bg-card/60 border-border hover:border-violet-500/40 transition-all" data-testid={`release-card-${item.track_id || item.reel_id || item.id}`}>
      <div className="relative aspect-square bg-muted/30">
        {cover ? (
          <img src={cover} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20">
            <Music className="w-12 h-12 text-muted-foreground/40" />
          </div>
        )}
        {/* Play button hover overlay */}
        <button
          onClick={() => onPlay?.(item)}
          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`release-play-${item.track_id || item.reel_id || item.id}`}
        >
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
            {isVideo ? <Video className="w-5 h-5 text-black ml-0.5" /> : <Play className="w-5 h-5 text-black ml-0.5" fill="currentColor" />}
          </div>
        </button>
        {/* Badges */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-1">
          {item.explicit && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/80 text-white">E</span>}
          {item.is_promoted && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-500 text-white">PROMO</span>}
          {(item.play_count || 0) > 100 && (
            <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-black/70 text-white flex items-center gap-1">
              <TrendingUp className="w-2.5 h-2.5" />
              {new Intl.NumberFormat().format(item.play_count)}
            </span>
          )}
        </div>
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold truncate" data-testid={`release-title-${item.track_id || item.reel_id || item.id}`}>{item.title || item.caption || 'Untitled'}</p>
        <p className="text-xs text-muted-foreground truncate">{item.artist_name || item.author_name || 'Artist'}</p>
        <div className="flex items-center gap-1 mt-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSave?.(item)} data-testid={`release-save-${item.track_id || item.reel_id || item.id}`}>
            <Heart className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onShare?.(item)} data-testid={`release-share-${item.track_id || item.reel_id || item.id}`}>
            <Share2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={() => onBuy?.(item)} data-testid={`release-buy-${item.track_id || item.reel_id || item.id}`}>
            <ShoppingBag className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ServiceCard({ item, onOpen }) {
  const Icon = SERVICE_ICONS[item.category] || Music;
  return (
    <Card className="p-3 rounded-2xl bg-card/60 border-border hover:border-emerald-500/40 hover:bg-card/80 transition-all cursor-pointer" onClick={() => onOpen?.(item)} data-testid={`service-card-${item.id}`}>
      <div className="flex items-start gap-3">
        {item.cover ? (
          <img src={item.cover} alt={item.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-emerald-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 mb-0.5">{item.category_label}</p>
          <p className="text-sm font-semibold truncate">{item.title || 'Untitled'}</p>
          <p className="text-xs text-muted-foreground truncate">{item.artist_name || 'Provider'}</p>
          {item.price != null && <p className="text-sm font-bold text-emerald-400 mt-1">${Number(item.price).toFixed(2)}</p>}
        </div>
      </div>
    </Card>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, onViewAll, testId }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4" data-testid={testId}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Icon className="w-4 h-4 text-violet-400" />
          <h2 className="text-lg sm:text-xl font-bold truncate">{title}</h2>
        </div>
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
      </div>
      {onViewAll && (
        <button onClick={onViewAll} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-0.5 whitespace-nowrap" data-testid={`${testId}-view-all`}>
          View all <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function NewReleasesPage() {
  const navigate = useNavigate();
  const { location: userLocation } = useUserLocation();

  const [genres, setGenres] = useState([]);
  const [activeGenre, setActiveGenre] = useState('all');
  const [sort, setSort] = useState('newest');
  const [hero, setHero] = useState([]);
  const [singles, setSingles] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch genres once
  useEffect(() => {
    fetch(`${API}/releases/genres`).then(r => r.json()).then(d => setGenres(d.genres || [])).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const genreParam = activeGenre === 'all' ? '' : `&genre=${encodeURIComponent(activeGenre)}`;
    const cityParam = sort === 'local' && userLocation?.city ? `&city=${encodeURIComponent(userLocation.city)}` : '';
    try {
      const [heroRes, singlesRes, albumsRes, videosRes, servicesRes] = await Promise.all([
        fetch(`${API}/releases/hero`).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/releases/singles?limit=12&sort=${sort}${genreParam}${cityParam}`).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/releases/albums?limit=12${genreParam}`).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/releases/videos?limit=8${genreParam}`).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/releases/services?limit=8`).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      setHero(heroRes.items || []);
      setSingles(singlesRes.items || []);
      setAlbums(albumsRes.items || []);
      setVideos(videosRes.items || []);
      setServices(servicesRes.items || []);
    } catch {
      toast.error('Failed to load releases');
    }
    setLoading(false);
  }, [activeGenre, sort, userLocation?.city]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePlay = (item) => {
    if (item.track_id) navigate(`/track/${item.track_id}`);
    else if (item.reel_id) navigate(`/reel/${item.reel_id}`);
  };
  const handleSave = () => toast.success('Saved to library');
  const handleShare = (item) => {
    const url = `${window.location.origin}${item.track_id ? `/track/${item.track_id}` : item.reel_id ? `/reel/${item.reel_id}` : ''}`;
    navigator.clipboard?.writeText(url).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed'));
  };
  const handleBuy = (item) => {
    if (item.dsp_url) window.open(item.dsp_url, '_blank');
    else toast.info('Support this artist by tipping or subscribing');
  };
  const handleOpenService = (svc) => svc.link && navigate(svc.link);

  const heroPromo = hero[0];

  return (
    <div className="min-h-screen bg-background text-foreground pt-20 pb-16" data-testid="new-releases-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Page title */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-orange-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">New Releases</h1>
            <p className="text-xs text-muted-foreground">Discover today&rsquo;s singles, albums, videos & creator drops.</p>
          </div>
        </div>

        {/* Featured Hero */}
        {heroPromo ? (
          <Card className="relative overflow-hidden rounded-3xl mb-6 aspect-[16/9] sm:aspect-[21/7]" data-testid="releases-hero">
            {heroPromo.cover_image ? (
              <img src={heroPromo.cover_image} alt={heroPromo.title} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-violet-700 via-fuchsia-700 to-orange-600" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
              <span className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-orange-500 text-white mb-2">Featured Promo</span>
              <h2 className="text-2xl sm:text-4xl font-bold mb-1 max-w-2xl">{heroPromo.title || 'New Music Drop'}</h2>
              <p className="text-sm text-white/80 max-w-2xl line-clamp-2 mb-3">{heroPromo.description || heroPromo.artist_name}</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => heroPromo.link && (heroPromo.link.startsWith('/') ? navigate(heroPromo.link) : window.open(heroPromo.link, '_blank'))}
                  className="bg-white text-black hover:bg-white/90 gap-1.5"
                  data-testid="releases-hero-cta"
                >
                  <Play className="w-4 h-4" fill="currentColor" /> Play Now
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="relative overflow-hidden rounded-3xl mb-6 p-6 sm:p-8 bg-gradient-to-br from-violet-700 via-fuchsia-700 to-orange-600" data-testid="releases-hero-cta-empty">
            <div className="max-w-2xl">
              <span className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-white/20 text-white mb-2">Featured Promo Slot</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Promote your next drop to millions.</h2>
              <p className="text-sm text-white/90 mb-4">Get top billing on the New Releases page. Artists can pay to promote a video, single, album, mixtape, livestream, or event.</p>
              <Button onClick={() => navigate('/promo/create')} className="bg-white text-black hover:bg-white/90 gap-1.5" data-testid="releases-hero-buy">
                <Zap className="w-4 h-4" /> Buy this slot
              </Button>
            </div>
          </Card>
        )}

        {/* Genre tabs */}
        <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6 mb-5" data-testid="releases-genres">
          <div className="flex gap-2 min-w-max">
            <GenreChip active={activeGenre === 'all'} label="All Genres" onClick={() => setActiveGenre('all')} testId="genre-all" />
            {genres.map(g => (
              <GenreChip
                key={g.slug}
                active={activeGenre === g.slug}
                label={g.label}
                count={g.count}
                onClick={() => setActiveGenre(g.slug)}
                testId={`genre-${g.slug}`}
              />
            ))}
          </div>
        </div>

        {/* Sort chips (applies to Singles) */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="releases-sorts">
          {SORTS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 border transition-colors ${sort === s.key ? 'bg-violet-500/15 text-violet-300 border-violet-500/40' : 'bg-card/40 text-muted-foreground border-border hover:text-foreground'}`}
                data-testid={`sort-${s.key}`}
              >
                <Icon className="w-3 h-3" /> {s.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
        ) : (
          <>
            {/* New Singles */}
            <section className="mb-10" data-testid="section-singles">
              <SectionHeader icon={Music} title="New Singles" subtitle={`Fresh drops · sorted by ${SORTS.find(s => s.key === sort)?.label.toLowerCase()}`} onViewAll={() => navigate('/tracks')} testId="header-singles" />
              {singles.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {singles.map(t => <ReleaseCard key={t.track_id} item={t} kind="single" onPlay={handlePlay} onSave={handleSave} onShare={handleShare} onBuy={handleBuy} />)}
                </div>
              ) : (
                <EmptyState msg="No new singles yet — check back soon!" testId="empty-singles" />
              )}
            </section>

            {/* New Albums */}
            <section className="mb-10" data-testid="section-albums">
              <SectionHeader icon={ShoppingBag} title="New Albums & Mixtapes" onViewAll={() => navigate('/tracks?type=album')} testId="header-albums" />
              {albums.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {albums.map(t => <ReleaseCard key={t.track_id} item={t} kind="album" onPlay={handlePlay} onSave={handleSave} onShare={handleShare} onBuy={handleBuy} />)}
                </div>
              ) : (
                <EmptyState msg="No albums yet in this genre." testId="empty-albums" />
              )}
            </section>

            {/* New Music Videos */}
            <section className="mb-10" data-testid="section-videos">
              <SectionHeader icon={Video} title="New Music Videos" onViewAll={() => navigate('/reels')} testId="header-videos" />
              {videos.length ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {videos.map(v => <ReleaseCard key={v.reel_id || v.id} item={v} kind="video" onPlay={handlePlay} onSave={handleSave} onShare={handleShare} onBuy={handleBuy} />)}
                </div>
              ) : (
                <EmptyState msg="No music videos yet in this genre." testId="empty-videos" />
              )}
            </section>

            {/* Creator Services Marketplace */}
            <section className="mb-10" data-testid="section-services">
              <SectionHeader icon={Palette} title="Creator Services & Promos" subtitle="Beats · Mixing · Music videos · Design · Photography · Merch · More" onViewAll={() => navigate('/marketplace')} testId="header-services" />
              {services.length ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {services.map(s => <ServiceCard key={s.id} item={s} onOpen={handleOpenService} />)}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border p-6 text-center" data-testid="empty-services">
                  <p className="text-sm text-muted-foreground mb-3">No services listed yet — creators, this is your spot.</p>
                  <Button onClick={() => navigate('/gigs/create')} className="gap-1.5" data-testid="services-list-cta">
                    <Zap className="w-4 h-4" /> List your service
                  </Button>
                </div>
              )}
            </section>

            {/* Promote CTA */}
            <Card className="p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-orange-500/10 via-card/50 to-card/50 border-orange-500/25" data-testid="releases-promote-cta">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-orange-400" />
                    <p className="text-[10px] uppercase tracking-widest font-bold text-orange-400">Promote Your Music</p>
                  </div>
                  <h3 className="text-lg font-bold mb-1">Want your drop on the front page?</h3>
                  <p className="text-xs text-muted-foreground max-w-xl">Buy a promo placement — hero, singles, albums, videos, or local city spotlight. Set your dates and start reaching listeners today.</p>
                </div>
                <Button onClick={() => navigate('/promo/create')} className="bg-gradient-to-r from-orange-500 to-fuchsia-600 hover:from-orange-600 hover:to-fuchsia-700 gap-1.5" data-testid="releases-promote-btn">
                  <Zap className="w-4 h-4" /> Buy a promo
                </Button>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function GenreChip({ active, label, count, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${active ? 'bg-violet-500/15 text-violet-300 border-violet-500/40' : 'bg-card/40 text-muted-foreground border-border hover:text-foreground'}`}
      data-testid={testId}
    >
      {label}
      {count > 0 && <span className="ml-1.5 opacity-60">{count}</span>}
    </button>
  );
}

function EmptyState({ msg, testId }) {
  return <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid={testId}>{msg}</div>;
}
